import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/schema';
import { loadBoard, type BoardEntry } from '../db/board';
import { imageSizes } from '../db/images';
import { matches } from '../search/match';
import type { Sized } from './masonry';

/**
 * Everything one screen of a room needs, read once.
 *
 * Split from the ordering below it because the drag sits between the two: it
 * needs the ids this returns, and the arrangement it hands back is what the
 * grid is actually laid out from.
 */
export function useBoardData(
  roomId: string | undefined,
  sectionId: string | undefined,
  /** A search, lowercased. Filtering flattens the room; see below. */
  filter?: string,
) {
  // Wrapped in an object so "still loading" and "no such room" stay distinct —
  // useLiveQuery returns undefined for both, and the difference is a flash of
  // "Room not found" on every navigation.
  const data = useLiveQuery(async () => {
    if (roomId === undefined) return undefined;

    const room = await db.rooms.get(roomId);
    const view = await loadBoard(roomId, sectionId);
    // Every cover in the room, not just this container's: a search reaches
    // items that are nested, and the masonry needs their proportions before
    // any of them decode.
    const sizes = await imageSizes([
      ...view.entries.flatMap(coverOf),
      ...view.roomItems.flatMap((item) =>
        item.coverImageId === undefined ? [] : [item.coverImageId],
      ),
    ]);
    return { room, view, sizes };
  }, [roomId, sectionId]);

  /**
   * Filtering flattens the whole room into one wall of matches. Sections drop
   * out: a search that only looked in the container you're standing in would
   * miss the thing you filed away, which is the one you can't find.
   */
  const entries = useMemo(() => {
    const view = data?.view;
    if (view === undefined) return [];
    if (filter === undefined) return view.entries;

    return view.roomItems
      .filter((item) => matches(item, filter))
      .sort((a, b) => b.sort - a.sort)
      .map((item): BoardEntry => ({ kind: 'item', id: item.id, sort: item.sort, item }));
  }, [data, filter]);

  const ids = useMemo(() => entries.map((entry) => entry.id), [entries]);
  const byId = useMemo(() => new Map(entries.map((entry) => [entry.id, entry])), [entries]);

  return { data, entries, ids, byId };
}

export type BoardData = ReturnType<typeof useBoardData>;

/**
 * The arrangement the grid is laid out from. Mid-drag that's the drag's, not
 * the database's — same entries, different sequence, and the masonry doesn't
 * care which it was given.
 */
export function useBoardOrder(source: BoardData, order: readonly string[]) {
  const { data, entries, ids, byId } = source;

  const ordered = useMemo(() => {
    if (order === ids) return entries;
    return order.flatMap((id) => {
      const entry = byId.get(id);
      return entry === undefined ? [] : [entry];
    });
  }, [entries, ids, byId, order]);

  // Aspect ratios come from the database, so the whole grid is computable
  // before a single photo has decoded. A section without a cover of its own is
  // square: a quad of four photos has no shape to inherit.
  const sized = useMemo<Sized[]>(
    () =>
      ordered.map((entry) => {
        if (entry.kind === 'section' && entry.section.coverImageId === undefined) {
          return { id: entry.id, w: 1, h: 1 };
        }

        const cover = coverOf(entry)[0];
        const size = cover === undefined ? undefined : data?.sizes.get(cover);
        return { id: entry.id, w: size?.w ?? 1, h: size?.h ?? 1 };
      }),
    [ordered, data],
  );

  return { ordered, sized };
}

/** The image that stands for an entry, if it has one. */
export function coverOf(entry: BoardEntry): string[] {
  if (entry.kind === 'item') {
    return entry.item.coverImageId === undefined ? [] : [entry.item.coverImageId];
  }
  if (entry.section.coverImageId !== undefined) return [entry.section.coverImageId];
  return entry.covers.slice(0, 1);
}
